import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import LabelValue from 'utils/components/LabelValue/LabelValue';
import LayoutShape, {
    LayoutComponentShape,
    LayoutContainerShape,
} from 'layout/LayoutShape';
import { TextInput, Button } from '@instinet-analytics/semantic-ui-components';
import { toDateString } from '../../SupportVisualisationHelpers';
import './SupportVisualisationLayout.scss';
import { UIConfiguration } from 'layout/uiConfiguration';
import { useAppLayoutOrigin } from 'layout/WellKnownLayoutOrigin';
import { selectWidgetComponentConfigs } from 'widgets/store/selectors';
import { useServices } from 'app/hooks';
import { useDispatchShowInfo } from 'notification/store/dispatcher';
import { useLayoutsList } from 'layout/store/useLayoutsList';
import { switchLayoutAsync } from 'layout/store/thunks';
import { useDispatch } from 'store/hooks';

export function SupportVisualisationLayout(props: VisualisationContextProps) {
    const { state } = props;
    const layout = state?.state.layout?.result;
    const currentOrigin = useAppLayoutOrigin();
    const { layoutService, widgetsService } = useServices();
    const dispatch = useDispatch();

    const { layoutList, forceLayoutListReload } = useLayoutsList(currentOrigin);

    const timeStamp = toDateString(state?.timeStamp);
    const userText = state?.state?.login?.result?.username
        ? ' for ' + state?.state?.login?.result?.username
        : '';

    const [name, setName] = useState(`Copy of Layout ${timeStamp}${userText}`);

    const dispatchShowInfo = useDispatchShowInfo();

    const onChangeName = (nextName: string) => {
        setName(nextName);
    };

    if (!layout) {
        return <div>None found in state</div>;
    }

    function getNextName() {
        let i = 0;
        let res = name;
        while (layoutList.find((l) => l.name === res)) {
            i++;
            res = `${name} (${i})`;
        }
        return res;
    }

    const onSave = (open: boolean) => {
        const saveName = getNextName();
        const components = selectWidgetComponentConfigs(
            state!.state,
            layout.components,
            widgetsService
        );
        const uiConfig: UIConfiguration = {
            name: saveName,
            origin: currentOrigin,
            id: null,
            layout: {
                components,
                shape: layout.shape,
                appSettings: state?.state.app.appSettings,
                sharedWidgetConfigs: state?.state.widgets.sharedConfig,
            },
        };
        layoutService
            .saveLayout(uiConfig)
            .then(() => {
                forceLayoutListReload();
                if (open) {
                    dispatch(switchLayoutAsync(currentOrigin, saveName));
                } else {
                    dispatchShowInfo('Layout saved as ' + saveName);
                }
            })
            .catch(() => {
                dispatchShowInfo('Error saving copy of layout');
            });
    };
    const onClickSave = () => {
        onSave(false);
    };

    const onClickSaveAndOpen = () => {
        onSave(true);
    };

    return (
        <div className={'SupportVisualisationLayout'}>
            <div>
                <div className="top">
                    <div>
                        <LabelValue
                            label="Id"
                            value={(layout.id ?? '').toString()}
                        />
                        <LabelValue label="Name" value={layout.name} />
                        <LabelValue label="Origin" value={layout.origin} />
                    </div>
                    <div className={'open-layout'}>
                        Copy Name:
                        <TextInput value={name} onChange={onChangeName} />
                        <Button primary onClick={onClickSave}>
                            Save Copy
                        </Button>
                        <Button primary onClick={onClickSaveAndOpen}>
                            Save Copy & Open
                        </Button>
                    </div>
                </div>
                {layout.shape && <LayoutShapePanel shape={layout.shape} />}
            </div>
        </div>
    );
}

export function LayoutShapePanel(props: { shape: LayoutShape }) {
    const { shape } = props;

    return (
        <div className="LayoutShape">
            <div className="label">Shape:</div>
            <LayoutShapeNode node={shape} />
        </div>
    );
}

export function LayoutShapeNode(props: { node: LayoutShape; indent?: number }) {
    const { node, indent } = props;

    if (node.type === 'component') {
        return <LayoutShapeComponentNode node={node} indent={indent ?? 0} />;
    }

    const container = node as LayoutContainerShape;
    const content = container.content || [];

    return (
        <div className="shape">
            <div className="title">
                {container.type}
                {container.activeItemIndex !== undefined
                    ? ` (active index = ${container.activeItemIndex})`
                    : ''}
            </div>
            {content.map((c, i) => (
                <LayoutShapeNode
                    key={i.toString()}
                    node={c}
                    indent={indent ?? 0 + 1}
                />
            ))}
        </div>
    );
}

export function LayoutShapeComponentNode(props: {
    node: LayoutComponentShape;
    indent: number;
}) {
    const { node, indent } = props;

    const style = { marginLeft: `${indent * 5}px`, marginBottom: '2px' };
    return (
        <div style={style}>
            {node.componentType} - {node.componentId}
        </div>
    );
}
